import { useEffect, useState } from "react";

import ReactJson from "react-json-view";

import {
	Alert,
	Box,
	Button,
	Card,
	CardActionArea,
	CardContent,
	CardMedia,
	Chip,
	CircularProgress,
	FormControl,
	FormControlLabel,
	Grid,
	InputLabel,
	MenuItem,
	Pagination,
	Select,
	Stack,
	Switch,
	TextField,
	Toolbar,
	Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";

import { runQuery } from "../../../apis/AiSearch";

const PAGE_SIZES = [10, 20, 50];

function Price({ product }) {
	const { original_price, discount_price } = product;
	const hasDiscount =
		discount_price != null && original_price != null && discount_price !== original_price;

	return (
		<Stack direction="row" alignItems="center" gap={1}>
			<Typography variant="body2" fontWeight="bold">
				₹{hasDiscount ? discount_price : original_price ?? "-"}
			</Typography>
			{hasDiscount && (
				<Typography
					variant="body2"
					sx={{ color: "#d32f2f", textDecorationLine: "line-through" }}
				>
					₹{original_price}
				</Typography>
			)}
		</Stack>
	);
}

function ResultCard({ image, href, title, subtitle, children }) {
	const body = (
		<>
			<CardMedia
				component="img"
				image={image}
				alt={title}
				sx={{ height: 240, objectFit: "contain", bgcolor: "#f5f5f5" }}
			/>
			<CardContent>
				<Typography variant="subtitle2" noWrap title={title}>
					{title || "-"}
				</Typography>
				<Typography variant="body2" color="text.secondary" noWrap>
					{subtitle || "-"}
				</Typography>
				{children}
			</CardContent>
		</>
	);

	return (
		<Card variant="outlined" sx={{ height: "100%" }}>
			{href ? (
				<CardActionArea href={href} target="_blank" rel="noopener noreferrer">
					{body}
				</CardActionArea>
			) : (
				body
			)}
		</Card>
	);
}

function ProductResult({ product }) {
	return (
		<ResultCard
			image={product.image?.file}
			href={product.url}
			title={product.name}
			subtitle={product.brand?.name}
		>
			<Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
				<Price product={product} />
				{product.out_of_stock && <Chip size="small" color="error" label="Out of stock" />}
			</Stack>
			<Typography variant="caption" color="text.secondary">
				#{product.id}
				{product.seller?.name ? ` · ${product.seller.name}` : ""}
			</Typography>
		</ResultCard>
	);
}

function OutfitResult({ outfit }) {
	return (
		<ResultCard
			image={outfit.image?.file}
			title={outfit.title}
			subtitle={outfit.owner?.name}
		>
			<Typography variant="caption" color="text.secondary">
				#{outfit.id} · {outfit.total_products ?? 0} products
			</Typography>
		</ResultCard>
	);
}

export default function AiSearchPage() {
	const [form, setForm] = useState({ query: "", imageUrl: "", raw: false });
	// The search that was last submitted; paging re-runs this, not the form.
	const [search, setSearch] = useState(null);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);

	const [response, setResponse] = useState(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!search) {
			return;
		}

		let cancelled = false;
		const fetchData = async () => {
			setLoading(true);
			const res = await runQuery({ ...search, page, pageSize });
			if (!cancelled) {
				setResponse(res);
				setLoading(false);
			}
		};

		fetchData();
		return () => {
			cancelled = true;
		};
	}, [search, page, pageSize]);

	const handleSubmit = (event) => {
		event.preventDefault();
		const query = form.query.trim();
		if (!query) {
			return;
		}
		setPage(1);
		setSearch({ query, imageUrl: form.imageUrl.trim(), raw: form.raw });
	};

	const handleFormChange = (name) => (event) => {
		const value = name === "raw" ? event.target.checked : event.target.value;
		setForm((prev) => ({ ...prev, [name]: value }));
	};

	const pageCount = response?.count ? Math.ceil(response.count / pageSize) : 0;

	const renderResults = () => {
		if (!response) {
			return null;
		}

		if (!response.success) {
			return <Alert severity="error">{response.message}</Alert>;
		}

		if (response.raw) {
			return (
				<ReactJson
					src={response.raw}
					collapsed={2}
					displayDataTypes={false}
					enableClipboard={true}
					name={false}
				/>
			);
		}

		return (
			<>
				<Stack direction="row" alignItems="center" gap={1} mb={2}>
					<Chip color="primary" label={response.intent} sx={{ textTransform: "capitalize" }} />
					<Typography variant="body2" color="text.secondary">
						{response.count} result{response.count === 1 ? "" : "s"} for &ldquo;{response.query}&rdquo;
					</Typography>
				</Stack>

				{response.results.length === 0 ? (
					<Alert severity="info">No results</Alert>
				) : (
					<Grid container spacing={2}>
						{response.results.map((item) => (
							<Grid item key={item.id} xs={12} sm={6} md={4} lg={3}>
								{response.intent === "outfit" ? (
									<OutfitResult outfit={item} />
								) : (
									<ProductResult product={item} />
								)}
							</Grid>
						))}
					</Grid>
				)}

				<Stack direction="row" justifyContent="flex-end" alignItems="center" gap={2} mt={3}>
					<FormControl size="small">
						<InputLabel>Per page</InputLabel>
						<Select
							value={pageSize}
							label="Per page"
							onChange={(event) => {
								setPageSize(event.target.value);
								setPage(1);
							}}
						>
							{PAGE_SIZES.map((size) => (
								<MenuItem key={size} value={size}>
									{size}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					{pageCount > 1 && (
						<Pagination
							count={pageCount}
							page={page}
							onChange={(_, value) => setPage(value)}
							color="primary"
						/>
					)}
				</Stack>
			</>
		);
	};

	return (
		<Grid container justifyContent={"center"}>
			<Grid item xs={12} md={10}>
				<Box sx={{ p: 5 }}>
					<Toolbar sx={{ px: 0 }}>
						<Typography variant="h4" noWrap>
							AI Search
						</Typography>
					</Toolbar>

					<Box component="form" onSubmit={handleSubmit} pb={3}>
						<Grid container spacing={2} alignItems="center">
							<Grid item xs={12} md={5}>
								<TextField
									fullWidth
									autoFocus
									label="Query"
									placeholder="e.g. red floral summer dress"
									value={form.query}
									onChange={handleFormChange("query")}
								/>
							</Grid>
							<Grid item xs={12} md={4}>
								<TextField
									fullWidth
									label="Image URL (optional)"
									value={form.imageUrl}
									onChange={handleFormChange("imageUrl")}
								/>
							</Grid>
							<Grid item xs={6} md={1.5}>
								<FormControlLabel
									control={
										<Switch
											checked={form.raw}
											onChange={handleFormChange("raw")}
										/>
									}
									label="Raw"
								/>
							</Grid>
							<Grid item xs={6} md={1.5}>
								<Button
									fullWidth
									type="submit"
									variant="contained"
									startIcon={<Search />}
									disabled={loading || !form.query.trim()}
								>
									Search
								</Button>
							</Grid>
						</Grid>
					</Box>

					{loading ? (
						<Stack alignItems="center" gap={2} py={8}>
							<CircularProgress />
							<Typography variant="body2" color="text.secondary">
								Searching… this can take up to a minute
							</Typography>
						</Stack>
					) : (
						renderResults()
					)}
				</Box>
			</Grid>
		</Grid>
	);
}
